import React, { useState, useEffect, useMemo } from 'react';
import { GoogleMap, MarkerF, Polyline, useJsApiLoader, OverlayViewF } from '@react-google-maps/api';
import { motion, AnimatePresence } from 'framer-motion';

interface NearbyLandmarksModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: any;
    landmarks: any[];
    categories: any[];
}

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
        {
            "featureType": "all",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#000000" }]
        },
        {
            "featureType": "all",
            "elementType": "labels.text.stroke",
            "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "weight": 2 }]
        },
        {
            "featureType": "landscape",
            "elementType": "all",
            "stylers": [{ "color": "#f2f2f2" }]
        },
        {
            "featureType": "road",
            "elementType": "all",
            "stylers": [{ "saturation": -100 }, { "lightness": 45 }]
        },
        {
            "featureType": "water",
            "elementType": "all",
            "stylers": [{ "color": "#e9e9e9" }, { "visibility": "on" }]
        }
    ]
};

const CATEGORY_ICONS: Record<string, string> = {
    school: "M12 3L1 9L12 15L21 9L12 3M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z",
    transport: "M18 11V15H17V16C17 16.55 16.55 17 16 17H15C14.45 17 14 16.55 14 16V15H10V16C10 16.55 9.55 17 9 17H8C7.45 17 7 16.55 7 16V15H6V11H18M16.5 7C17.33 7 18 7.67 18 8.5V10H6V8.5C6 7.67 6.67 7 7.5 7H16.5M8 13C8.55 13 9 12.55 9 12C9 11.45 8.55 11 8 11C7.45 11 7 11.45 7 12C7 12.55 7.45 13 8 13M16 13C16.55 13 17 12.55 17 12C17 11.45 16.55 11 16 11C15.45 11 15 11.45 15 12C15 12.55 15.45 13 16 13Z",
    hospital: "M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M18 13H14V17H10V13H6V9H10V5H14V9H18V13Z",
    bank: "M11.5 1L2 6V8H21V6L11.5 1M5 10V17H8V10H5M10 10V17H13V10H10M15 10V17H18V10H15M2 19V21H21V19H2Z",
    temple: "M12 2C12 2 4 10 4 15C4 18.31 6.69 21 10 21H14C17.31 21 20 18.31 20 15C20 10 12 2 12 2Z",
    atm: "M2 4V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V4C22 2.9 21.1 2 20 2H4C2.9 2 2 2.9 2 4M20 18H4V12H20V18M20 8H4V4H20V8Z",
    shopping: "M7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5.48C20.96 5.34 21 5.17 21 5C21 4.45 20.55 4 20 4H5.21L4.27 2H1ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18Z"
};

const getMapIconPath = (catId: string) => {
    const key = catId.toLowerCase().trim();
    if (key.includes('school') || key.includes('college') || key.includes('academy') || key.includes('education')) return CATEGORY_ICONS.school;
    if (key.includes('transport') || key.includes('bus') || key.includes('metro') || key.includes('station') || key.includes('airport')) return CATEGORY_ICONS.transport;
    if (key.includes('hospital') || key.includes('medical') || key.includes('clinic')) return CATEGORY_ICONS.hospital;
    if (key.includes('bank')) return CATEGORY_ICONS.bank;
    if (key.includes('temple') || key.includes('church') || key.includes('mosque')) return CATEGORY_ICONS.temple;
    if (key.includes('atm')) return CATEGORY_ICONS.atm;
    if (key.includes('shopping') || key.includes('mall') || key.includes('market')) return CATEGORY_ICONS.shopping;
    return "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z";
};

const NearbyLandmarksModal: React.FC<NearbyLandmarksModalProps> = ({ isOpen, onClose, project, landmarks, categories }) => {
    const [activeCategory, setActiveCategory] = useState(categories[0]?.id || 'school');
    const [selectedLandmark, setSelectedLandmark] = useState<any>(null);
    const [showAllInCategory, setShowAllInCategory] = useState(false);
    const [map, setMap] = useState<google.maps.Map | null>(null);

    useEffect(() => {
        setShowAllInCategory(false);
    }, [activeCategory]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyAKUwG8_kZORARJWNJeWyjT5m87jL5eZNI"
    });

    const projectCenter = useMemo(() => ({
        lat: parseFloat(project.mapDetails?.latitude || '19.0760'),
        lng: parseFloat(project.mapDetails?.longitude || '72.8777')
    }), [project]);

    // Generate deterministic mock coordinates
    const plottedLandmarks = useMemo(() => {
        return landmarks.map((l, idx) => {
            const distance = parseFloat(l.distance) || 1;
            const angle = (idx * 137.5) % 360;
            const offsetLat = (distance / 111) * Math.cos(angle * Math.PI / 180);
            const offsetLng = (distance / (111 * Math.cos(projectCenter.lat * Math.PI / 180))) * Math.sin(angle * Math.PI / 180);

            return {
                ...l,
                position: {
                    lat: projectCenter.lat + offsetLat,
                    lng: projectCenter.lng + offsetLng
                }
            };
        });
    }, [landmarks, projectCenter]);

    const filteredPlotted = plottedLandmarks.filter(l => {
        const cat = l.category || l.type || 'Landmark';
        return cat.toLowerCase().trim() === activeCategory.toLowerCase().trim();
    });

    useEffect(() => {
        if (selectedLandmark && map) {
            map.panTo(selectedLandmark.position);
            map.setZoom(15);
        }
    }, [selectedLandmark, map]);

    if (!isOpen || !isLoaded) return null;

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-white flex flex-col md:flex-row overflow-hidden"
        >
            {/* Sidebar */}
            <div className="w-full md:w-[420px] bg-white border-t md:border-t-0 md:border-r border-slate-100 flex flex-col h-[60vh] md:h-full shadow-2xl z-20 order-2 md:order-1">
                {/* Header */}
                <div className="p-4 md:p-6 border-b border-slate-50 flex items-center justify-between bg-white sticky top-0 z-30">
                    <div>
                        <h2 className="text-lg md:text-2xl font-bold text-black tracking-tight leading-tight mb-2">{project.name}</h2>
                        <p className="text-[10px] md:text-[12px] font-bold text-black flex items-center gap-2">
                            <i className="fa-solid fa-location-dot"></i>
                            {project.location}, {project.city}
                        </p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-slate-100 flex items-center justify-center text-black hover:bg-black hover:text-white transition-all shadow-sm"
                    >
                        <i className="fa-solid fa-xmark text-sm md:text-base"></i>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar p-4 md:p-6 bg-white">
                    <div className="space-y-3">
                        {categories.map(cat => {
                            const isActive = activeCategory === cat.id;
                            const catLandmarks = plottedLandmarks.filter(l => {
                                const lCat = l.category || l.type || 'Landmark';
                                return lCat.toLowerCase().trim() === cat.id.toLowerCase().trim();
                            });

                            return (
                                <div key={cat.id} className="border-b border-slate-50 last:border-0 pb-3">
                                    <button
                                        onClick={() => {
                                            setActiveCategory(cat.id);
                                            setSelectedLandmark(null);
                                        }}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-all text-left relative overflow-hidden group/cat ${
                                            isActive 
                                            ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] shadow-xl scale-[1.02] z-10 border border-[#f1ae24]/30' 
                                            : 'bg-slate-50 border border-slate-100 hover:border-[#05144b]/20'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5 relative z-10">
                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                                                isActive ? 'bg-white text-black' : 'bg-white text-black shadow-sm'
                                            }`}>
                                                <i className={`fa-solid ${cat.icon} text-[9px]`}></i>
                                            </div>
                                            <div>
                                                <span className="text-[12px] md:text-[14px] font-bold block">
                                                    {cat.label}
                                                </span>
                                                <p className={`text-[10px] font-bold leading-none mt-1 ${isActive ? 'text-white' : 'text-slate-500'}`}>
                                                    {catLandmarks.length} Places
                                                </p>
                                            </div>
                                        </div>
                                        <i className={`fa-solid fa-chevron-right text-[9px] transition-transform relative z-10 ${isActive ? 'rotate-90' : 'opacity-20'}`}></i>
                                        
                                        {isActive && (
                                            <motion.div 
                                                layoutId="catFill"
                                                className="absolute inset-0 bg-gradient-to-r from-[#05144b] to-[#4c1d95] z-0"
                                                initial={{ y: '100%' }}
                                                animate={{ y: 0 }}
                                                transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                                            />
                                        )}
                                    </button>

                                    <AnimatePresence>
                                        {isActive && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-3 space-y-2 px-2">
                                                    {(showAllInCategory ? catLandmarks : catLandmarks.slice(0, 3)).map((place: any, idx: number) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setSelectedLandmark(place)}
                                                            className={`w-full text-left p-3 rounded-md border transition-all relative overflow-hidden ${
                                                                selectedLandmark === place 
                                                                ? 'border-[#f1ae24] shadow-md' 
                                                                : 'bg-white border-slate-100 hover:border-[#05144b]/10'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-between relative z-10">
                                                                <h4 className={`text-[12px] font-bold tracking-tight transition-colors ${
                                                                    selectedLandmark === place ? 'text-white' : 'text-black'
                                                                }`}>{place.name}</h4>
                                                                <span className={`text-[10px] font-bold transition-colors ${
                                                                    selectedLandmark === place ? 'text-white' : 'text-slate-500'
                                                                }`}>{place.distance ? (String(place.distance).toLowerCase().includes('km') ? place.distance : `${place.distance} KM`) : '0.5 KM'}</span>
                                                            </div>
                                                            {selectedLandmark === place && (
                                                                <motion.div 
                                                                    layoutId="placeFill"
                                                                    className="absolute inset-0 bg-gradient-to-r from-[#05144b] to-[#4c1d95] z-0"
                                                                    initial={{ x: '-100%' }}
                                                                    animate={{ x: 0 }}
                                                                    transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                                                                />
                                                            )}
                                                        </button>
                                                    ))}
                                                    {catLandmarks.length > 3 && (
                                                        <button onClick={(e) => { e.stopPropagation(); setShowAllInCategory(!showAllInCategory); }} className="w-full py-2.5 text-[11px] font-bold text-black hover:bg-slate-50 transition-colors border border-dashed border-slate-300 rounded-lg mt-2">
                                                            {showAllInCategory ? 'View Less' : `View All (${catLandmarks.length})`}
                                                        </button>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Map Area */}
            <div className="flex-1 relative h-[50vh] md:h-full">
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={projectCenter}
                    zoom={14}
                    options={mapOptions}
                    onLoad={map => setMap(map)}
                >
                    {/* Project Pulse Overlay */}
                    <OverlayViewF
                        position={projectCenter}
                        mapPaneName="overlayMouseTarget"
                    >
                        <div className="relative flex items-center justify-center translate-x-[-50%] translate-y-[-50%] pointer-events-none">
                            <motion.div 
                                animate={{ scale: [1, 2, 1], opacity: [0.5, 0, 0.5] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="absolute w-12 h-12 bg-black rounded-full"
                            />
                            <motion.div 
                                animate={{ scale: [1, 3, 1], opacity: [0.3, 0, 0.3] }}
                                transition={{ repeat: Infinity, duration: 3, delay: 0.5 }}
                                className="absolute w-16 h-16 border-2 border-black rounded-full"
                            />
                        </div>
                    </OverlayViewF>

                    <MarkerF
                        position={projectCenter}
                        zIndex={100}
                        icon={{
                            path: "M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z",
                            fillColor: "#05144b",
                            fillOpacity: 1,
                            strokeWeight: 4,
                            strokeColor: "#f1ae24",
                            scale: 1.8,
                            anchor: new google.maps.Point(12, 12)
                        }}
                    />

                    {plottedLandmarks.filter(l => {
                        const cat = l.category || l.type || 'Landmark';
                        return cat.toLowerCase().trim() === activeCategory.toLowerCase().trim();
                    }).map((place, idx) => {
                        const iconPath = getMapIconPath(activeCategory);
                        const isSelected = selectedLandmark === place;
                        return (
                            <React.Fragment key={idx}>
                                {isSelected && (
                                    <OverlayViewF
                                        position={place.position}
                                        mapPaneName="overlayMouseTarget"
                                    >
                                        <div className="relative flex items-center justify-center translate-x-[-50%] translate-y-[-50%] pointer-events-none">
                                            <motion.div 
                                                animate={{ scale: [1, 2.5], opacity: [0.6, 0] }}
                                                transition={{ repeat: Infinity, duration: 1.5 }}
                                                className="absolute w-10 h-10 bg-black rounded-full"
                                            />
                                            <motion.div 
                                                animate={{ scale: [1, 2], opacity: [0.4, 0] }}
                                                transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                                                className="absolute w-8 h-8 border-2 border-black rounded-full"
                                            />
                                        </div>
                                    </OverlayViewF>
                                )}
                                <MarkerF
                                    position={place.position}
                                    onClick={() => setSelectedLandmark(place)}
                                    zIndex={isSelected ? 10 : 1}
                                    icon={{
                                        path: iconPath,
                                        fillColor: isSelected ? "#000000" : "#ffffff",
                                        fillOpacity: 1,
                                        strokeWeight: isSelected ? 3 : 2,
                                        strokeColor: "#000000",
                                        scale: isSelected ? 2.2 : 1.5,
                                        anchor: new google.maps.Point(12, 12),
                                    }}
                                />
                            </React.Fragment>
                        );
                    })}

                    {selectedLandmark && (
                        <Polyline
                            path={[projectCenter, selectedLandmark.position]}
                            options={{
                                strokeColor: "#000000",
                                strokeOpacity: 0.8,
                                strokeWeight: 2,
                                icons: [{
                                    icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 },
                                    offset: '100%',
                                    repeat: '30px'
                                }]
                            }}
                        />
                    )}

                    <AnimatePresence>
                        {selectedLandmark && (
                            <motion.div
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 50, opacity: 0 }}
                                className="absolute bottom-6 md:bottom-10 right-4 md:right-10 left-4 md:left-auto md:w-[350px] bg-white rounded-xl shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-100 overflow-hidden z-50"
                            >
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-base font-bold text-black tracking-tight">{selectedLandmark.name}</h3>
                                        <button onClick={() => setSelectedLandmark(null)} className="text-slate-400 hover:text-black">
                                            <i className="fa-solid fa-xmark text-sm"></i>
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <i className="fa-solid fa-car-side text-lg mb-2 block"></i>
                                            <span className="text-[9px] font-black block text-black/40">Car</span>
                                            <span className="text-[10px] font-black block text-black">{Math.round(parseFloat(selectedLandmark.distance) * 4)}m</span>
                                        </div>
                                        <div className="text-center p-3 bg-black text-white rounded-xl shadow-xl shadow-black/20">
                                            <i className="fa-solid fa-walking text-lg mb-2 block"></i>
                                            <span className="text-[9px] font-black block opacity-60">Walk</span>
                                            <span className="text-[10px] font-black block">{Math.round(parseFloat(selectedLandmark.distance) * 12)}m</span>
                                        </div>
                                        <div className="text-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <i className="fa-solid fa-bicycle text-lg mb-2 block"></i>
                                            <span className="text-[9px] font-black block text-black/40">Bike</span>
                                            <span className="text-[10px] font-black block text-black">{Math.round(parseFloat(selectedLandmark.distance) * 6)}m</span>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-slate-50 flex items-center justify-between">
                                        <span className="text-[11px] font-bold text-slate-500">Est. Distance</span>
                                        <span className="text-base font-bold text-black">{selectedLandmark.distance ? (String(selectedLandmark.distance).toLowerCase().includes('km') ? selectedLandmark.distance : `${selectedLandmark.distance} KM`) : '0.5 KM'}</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </GoogleMap>
            </div>
        </motion.div>
    );
};

export default NearbyLandmarksModal;
